import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { SellerBadgeStack } from "@/components/seller-badge-stack";
import { fetchPublicSeller, type PublicSeller } from "@/lib/public-sellers";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n";

/**
 * Lightweight hover / tap popover that surfaces seller trust signals
 * anywhere a seller name or avatar is displayed. Reuses the shared
 * `SellerBadgeStack` — no duplicated badge logic.
 */
export function SellerHoverCard({
  sellerId,
  children,
  className,
  preloaded,
}: {
  sellerId: string | null | undefined;
  children: ReactNode;
  className?: string;
  /** Skip the query when the parent already has the row. */
  preloaded?: PublicSeller | null;
}) {
  const { t } = useTranslation("marketing");
  const enabled = !!sellerId && !preloaded;
  const { data } = useQuery({
    queryKey: ["hovercard-seller", sellerId],
    queryFn: () => fetchPublicSeller(sellerId as string),
    enabled,
    staleTime: 60_000,
  });
  const seller = preloaded ?? data ?? null;

  if (!sellerId) return <>{children}</>;

  const name = seller?.display_name || seller?.shop_name || seller?.username || t("hoverCard.fallbackName", "Seller");
  const initials = name.slice(0, 2).toUpperCase();
  const memberSince = seller?.joined_at
    ? new Date(seller.joined_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })
    : null;
  const sales = seller?.seller_sales_count ?? 0;
  const positive = seller?.seller_positive_pct;

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <span className={cn("cursor-pointer", className)}>{children}</span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-72 border-white/10 bg-background/95 backdrop-blur-xl"
      >
        <div className="flex items-start gap-3">
          <Avatar className="h-11 w-11 border border-white/10">
            <AvatarImage src={seller?.avatar_url ?? undefined} alt={name} />
            <AvatarFallback className="bg-gold/10 text-gold text-xs font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-sm">{name}</p>
            <div className="mt-1">
              <SellerBadgeStack sellerId={sellerId} size="sm" />
            </div>
          </div>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
          <Stat label={t("hoverCard.successfulSales", "Successful sales")} value={sales.toLocaleString()} />
          <Stat
            label={t("hoverCard.positiveFeedback", "Positive feedback")}
            value={typeof positive === "number" ? `${Math.round(positive)}%` : "—"}
          />
          <Stat label={t("hoverCard.memberSince", "Member since")} value={memberSince ?? "—"} />
          <Stat
            label={t("hoverCard.rating", "Rating")}
            value={
              seller?.seller_rating != null
                ? `${seller.seller_rating.toFixed(1)}★`
                : "—"
            }
          />
        </dl>
        <div className="mt-3">
          <Button asChild size="sm" variant="outline" className="h-8 w-full text-xs">
            <Link
              to="/seller/$sellerId"
              params={{ sellerId: seller?.username ?? sellerId }}
            >
              {t("hoverCard.viewProfile", "View profile")}
            </Link>
          </Button>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-white/5 bg-white/[0.02] px-2 py-1.5">
      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-xs font-medium text-foreground">{value}</dd>
    </div>
  );
}
